import {AdminUser, CompletePayroll, Employee, EmploymentStatus, Payroll, PayrollStatus, SalaryChangeStatus} from "../app/interfaces";
import Cookie from 'js-cookie';
import {
    EMPLOYMENT_STATUS_ACTIVE,
    EMPLOYMENT_STATUS_EOR_QUOTE_APPROVED,
    EMPLOYMENT_STATUS_EOR_QUOTE_PROVIDED,
    EMPLOYMENT_STATUS_EOR_QUOTE_REQUESTED,
    EMPLOYMENT_STATUS_IN_REGISTRATION,
    EMPLOYMENT_STATUS_PENDING_EMPLOYEE_SIGNATURE,
    EMPLOYMENT_STATUS_PENDING_REGISTRATION_PAYMENT,
    EMPLOYMENT_STATUS_TERMINATED,
    EMPLOYMENT_STATUS_TERMINATION_REQUEST,
    EOR_SALARY_CHANGE_STATUS_REQUESTED,
    PAYROLL_STATUS_BONUS_PROVIDED,
    PAYROLL_STATUS_EOR_PARTNER_PAID,
    PAYROLL_STATUS_PAID, PAYROLL_STATUS_PENDING_COMPANY_PAYMENT,
    PAYROLL_STATUS_PENDING_EOR_PARTNER_PAYMENT,
    PAYROLL_STATUS_PENDING_PAYSLIP,
    PAYROLL_STATUS_PREPARING
} from "./constants";
import {defaultTheme} from "../styles/themes/default";

export const capitalize = (s: string | undefined | null) => (s && s[0].toUpperCase() + s.slice(1)) || ""

export const toSingular = (s: string) => s.slice(0, -1);

export const fullNameForUser = (user: Employee | AdminUser | null | undefined) => capitalize(user?.first_name) + ' ' + capitalize(user?.last_name)

export const isValidString = (s: any) : Boolean => {
    if (s === null) {
        return false;
    }
    if (typeof s === 'string' || (typeof s === 'object' && s.constructor === String)) {
        return s.length > 0;
    }
    return false;
}

export const isValidObject = (object: any) : Boolean => {
    if (object === null) {
        return false;
    }
    return typeof object === 'object';
}

export const isValidEmail = (email:any): Boolean => {
    if (email === null) {
        return false;
    }
    if (!isValidString(email)) {
        return false;
    }

    const re =
        /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    return re.test(email);
}

// Arrays pagination
export interface PaginationSummary {
    from: number;
    to: number;
    total: number;
    hasNextPage: boolean,
    hasPreviousPage: boolean
}

export interface Pagination<T>{
    items: T[],
    summary: PaginationSummary
}

export const paginateArray = <T>(array: T[], currentPage:number, pageSize:number): Pagination<T> => {
    const start = (currentPage - 1) * pageSize;
    const end = start + pageSize;
    const items = array.slice(start, end);

    const totalItems = array.length;

    const summary: PaginationSummary = {
        from: start + 1,
        to: Math.min(end, totalItems),
        total: totalItems,
        hasNextPage: currentPage < Math.ceil(totalItems / pageSize),
        hasPreviousPage: currentPage > 1,
    }
    return { items, summary };
}

export const getCurrentAuth = () => {
    const authCookie = Cookie.get('companio_work_auth');
    if (authCookie) {
        return JSON.parse(authCookie);
    }
    return null;
}

export const currentAccessToken = () => {
    if (isValidObject(getCurrentAuth()) && getCurrentAuth().hasOwnProperty('access_token')) {
        return getCurrentAuth().access_token
    }
    return null
}

export const shortDescriptionForStatus = (status:EmploymentStatus) : string => {
    switch (status){
        case EMPLOYMENT_STATUS_EOR_QUOTE_REQUESTED :
            return 'quote_was_requested'
        case EMPLOYMENT_STATUS_EOR_QUOTE_PROVIDED :
            return 'quote_under_review'
        case EMPLOYMENT_STATUS_EOR_QUOTE_APPROVED :
            return 'quote_was_approved'
        case EMPLOYMENT_STATUS_IN_REGISTRATION :
            return 'in_registration_process'
        case EMPLOYMENT_STATUS_PENDING_EMPLOYEE_SIGNATURE:
            return 'employee_signature_pending'
        case EMPLOYMENT_STATUS_TERMINATION_REQUEST:
            return 'employee_resignation_was_requested'
        case EMPLOYMENT_STATUS_TERMINATED:
            return 'employee_has_resigned'
        case EMPLOYMENT_STATUS_PENDING_REGISTRATION_PAYMENT:
            return 'awaiting_employee_payment_registration'
    }
    return '-'
}

// Arrays pagination

/*export const hasPendingPayrolls = (employee: Employee) => {
    let hasPendingPayroll = false;
    for(const payroll of employee.payrolls){
        if(payroll.status === PAYROLL_STATUS_EOR_REQUESTED){
            hasPendingPayroll = true;
        }
    }
    return hasPendingPayroll;
}*/

export const isValidNumber = (value:any) => {
    if (value === null) { return false }
    return !isNaN(parseFloat(value))
}

export const isPositiveNumber = (value:any) => {
    if (value === null) { return false }
    return isValidNumber(value) && value > 0
}

export const isNonNegativeNumber = (value:any) => {
    if (value === null) { return false }
    return isValidNumber(value) && value >= 0
}

export const hasBonus = (payroll: Payroll): Boolean => {
    if (payroll.hasOwnProperty('bonus')) {
        const {amount, currency, description} = payroll.bonus;
        return isPositiveNumber(amount) && isValidString(currency) && isValidString(description)
    }
    return false;
}

export const isCompletePayroll = (payroll: Payroll): payroll is CompletePayroll =>
    !!payroll?.eor_employee && !!payroll?.company

export function getSafeLocale(locale: string):string {
    if(isValidString(locale)){
        return locale.toLowerCase().substr(0, 2)
    }
    return 'en';
}

export const colorForStatus = (status: EmploymentStatus | PayrollStatus | SalaryChangeStatus): { background: string, color: string } => {
    switch (status) {
        case EMPLOYMENT_STATUS_EOR_QUOTE_REQUESTED:
            return {background: defaultTheme.purple, color: defaultTheme.white}
        case EMPLOYMENT_STATUS_IN_REGISTRATION:
            return {background: defaultTheme.yellow, color: defaultTheme.white}
        case EMPLOYMENT_STATUS_EOR_QUOTE_PROVIDED:
            return {background: defaultTheme.white, color: defaultTheme.grayDark}
        case EMPLOYMENT_STATUS_EOR_QUOTE_APPROVED:
            return {background: defaultTheme.blue, color: defaultTheme.white}
        case EOR_SALARY_CHANGE_STATUS_REQUESTED:
            return {background: defaultTheme.orange, color: defaultTheme.white}
        case EMPLOYMENT_STATUS_ACTIVE:
            return {background: defaultTheme.green, color: defaultTheme.white}
        case EMPLOYMENT_STATUS_TERMINATION_REQUEST:
            return {background: defaultTheme.grayDark, color: defaultTheme.white}
        case EMPLOYMENT_STATUS_PENDING_EMPLOYEE_SIGNATURE:
        case PAYROLL_STATUS_PENDING_COMPANY_PAYMENT:
            return {background: defaultTheme.red, color: defaultTheme.white}
        case EMPLOYMENT_STATUS_TERMINATED:
            return {background: defaultTheme.black, color: defaultTheme.white}
        case PAYROLL_STATUS_PAID:
            return {background: defaultTheme.green, color: defaultTheme.white}
        case PAYROLL_STATUS_PREPARING:
        case EMPLOYMENT_STATUS_PENDING_REGISTRATION_PAYMENT:
            return {background: defaultTheme.yellow, color: defaultTheme.white}
        case PAYROLL_STATUS_EOR_PARTNER_PAID:
            return {background: defaultTheme.blue, color: defaultTheme.white}
        case PAYROLL_STATUS_PENDING_PAYSLIP:
            return {background: defaultTheme.grayDark, color: defaultTheme.white}
        case PAYROLL_STATUS_PENDING_EOR_PARTNER_PAYMENT:
            return {background: defaultTheme.red, color: defaultTheme.white}
        case PAYROLL_STATUS_BONUS_PROVIDED:
            return {background: defaultTheme.purple, color: defaultTheme.white}
        default:
            return {background: defaultTheme.grayExtraLight, color: defaultTheme.gray}
    }
}

